import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Phone, Mail, MapPin, Clock, CheckCircle, 
  Send, Calendar, Wrench, AlertCircle, Star
} from 'lucide-react';
import { Helmet } from 'react-helmet';

// import emailjs from '@emailjs/browser'; // Not needed with iframe form
const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
    emergency: false
  });
// emailjs.init("ZIDhUQhBhLmIUKTkv"); // Not needed with iframe form
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false); // Add this state
  const [errors, setErrors] = useState({}); 
  const services = [
    'Emergency Plumbing',
    'Drain Cleaning',
    'Water Heater Service',
    'Heating System Repair',
    'Air Conditioning Service',
    'Boiler Installation',
    'Oil-to-Gas Conversion',
    'General Maintenance',
    'Other'
  ];

  const serviceAreas = [
    'Mount Olive', 'Budd Lake', 'Hackettstown', 'Netcong', 'Flanders',
    'Chester', 'Randolph', 'Succasunna', 'Roxbury', 'Stanhope', 'Long Valley',
    'Dover', 'Wharton', 'Rockaway', 'Denville', 'Boonton'
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
   
// The email the estimate request should be sent to.
const RECIPIENT_EMAIL = 'mountoliveplumbing@gmail.com';

const handleSubmit = (e) => {
  e.preventDefault();

  // Validate required fields
  const newErrors = {};
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  // Phone must be exactly 10 digits. Common formatting characters
  // (spaces, dashes, parentheses, dots) are stripped before checking.
  const phoneDigits = formData.phone.replace(/[\s\-().]/g, '');
  const phoneRegex = /^\d{10}$/;

  if (!formData.name.trim()) newErrors.name = 'Please enter your name.';

  if (!formData.email.trim()) {
    newErrors.email = 'Please enter your email.';
  } else if (!emailRegex.test(formData.email.trim())) {
    newErrors.email = 'Please enter a valid email address.';
  }

  if (!formData.phone.trim()) {
    newErrors.phone = 'Please enter your phone number.';
  } else if (!phoneRegex.test(phoneDigits)) {
    newErrors.phone = 'Please enter a valid 10-digit phone number.';
  }

  if (!formData.service) newErrors.service = 'Please select a service.';

  setErrors(newErrors);
  if (Object.keys(newErrors).length > 0) return;

  setIsSubmitting(true);

  // Build the email the visitor's default mail app will open with, pre-filled.
  const subject = `${formData.emergency ? '[EMERGENCY] ' : ''}Estimate Request - ${formData.service}`;

  const bodyLines = [
    `Name: ${formData.name}`,
    `Phone: ${formData.phone}`,
    `Email: ${formData.email}`,
    `Service Needed: ${formData.service}`,
    formData.emergency ? 'Emergency: YES - urgent response requested' : '',
    ...(formData.message.trim() ? ['', 'Project Details:', formData.message] : []),
    '',
    '---',
    `Submitted from mtoliveplumbing.com on ${new Date().toLocaleString('en-US', {
      timeZone: 'America/New_York',
      dateStyle: 'medium',
      timeStyle: 'short'
    })}`
  ].filter(Boolean);

  const mailtoLink = `mailto:${RECIPIENT_EMAIL}?subject=${encodeURIComponent(
    subject
  )}&body=${encodeURIComponent(bodyLines.join('\n'))}`;

  // Open the default mail app with everything pre-filled.
  window.location.href = mailtoLink;

  // Show the confirmation state, then reset.
  setIsSubmitting(false);
  setIsSubmitted(true);
  setTimeout(() => {
    setIsSubmitted(false);
    setFormData({
      name: '',
      email: '',
      phone: '',
      service: '',
      message: '',
      emergency: false
    });
  }, 5000);
};
  return (
    <div className="min-h-screen bg-white pt-20">
      <Helmet>
  <title>Contact Us | 24/7 Plumbing, Heating & AC in Mount Olive, NJ</title>
  <meta name="description" content="Call 973-862-0606 or request service online. Serving Mount Olive, Budd Lake, and Morris County with expert plumbing and HVAC care." />
  
  <meta property="og:title" content="Contact Mount Olive Plumbing – 24/7 Service" />
  <meta property="og:description" content="Need emergency plumbing or HVAC service? Reach out anytime — we're here to help." />
  <meta property="og:url" content="https://www.mtoliveplumbing.com/contact" />
</Helmet>
      {/* Hero Section */}
     <section className="relative py-20 text-white overflow-hidden">
  {/* Background Image - adjusted for better visibility */}
  <div
    className="absolute inset-0 bg-cover bg-center bg-no-repeat"
    style={{ 
      backgroundImage: "url('/images/bg-about_hero.webp')",
      filter: "brightness(1.2)"  // Brighten the image
    }}
  ></div>
  
  {/* Lighter overlay with gradient for better image visibility */}
  <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
  
  <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <motion.div
      className="text-center space-y-8"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
    >
      <motion.div
             className="inline-flex items-center space-x-2 text-white px-6 py-3 rounded-full font-semibold backdrop-blur-sm bg-white/20 border border-white/60"
             whileHover={{ scale: 1.05 }}
           >
        <Phone className="h-5 w-5" />
        <span>24/7 Emergency Service Available</span>
      </motion.div>
      
      <h1 className="text-5xl lg:text-6xl font-bold leading-tight drop-shadow-lg">
        Contact
        <span className="bg-gradient-to-r from-cyan-300 to-blue-300 bg-clip-text text-transparent block">
          Mount Olive Plumbing
        </span>
      </h1>
      
      <p className="text-xl text-white/95 max-w-3xl mx-auto leading-relaxed drop-shadow-md">
        Ready to get started? Contact us today for a free estimate or emergency service. We're here to help with all your plumbing, heating, and air conditioning needs.
      </p>
      
      <div className="flex flex-col sm:flex-row gap-6 justify-center">
        <motion.a
          href="tel:+1-(973) 862-0606"
          className="inline-flex items-center space-x-3 bg-white text-red-700 px-8 py-4 rounded-full font-bold text-lg shadow-2xl hover:shadow-white/25 transition-all duration-300"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Phone className="h-6 w-6" />
          <span>Emergency:(973) 862-0606</span>
        </motion.a>
        
        {/* <motion.a
          href="mailto:mountoliveplumbing@gmail.com"
             className="inline-flex items-center space-x-2 text-white px-6 py-3 rounded-full font-semibold backdrop-blur-sm bg-white/20 border border-white/60"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Mail className="h-5 w-5" />
          <span>Email Us</span>
        </motion.a> */}
      </div>
    </motion.div>
  </div>
</section>

      {/* Contact Form & Info */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form - opens the visitor's default mail app pre-filled */}
  <motion.div
    className="bg-gradient-to-br from-blue-50 to-white rounded-3xl shadow-2xl border border-blue-100 p-6 sm:p-8"
    initial={{ opacity: 0, x: -30 }}
    whileInView={{ opacity: 1, x: 0 }}
    viewport={{ once: true }}
  >
    {isSubmitted ? (
      <div className="flex flex-col items-center justify-center text-center py-16 h-full">
        <div className="bg-green-100 p-4 rounded-full mb-4">
          <CheckCircle className="h-12 w-12 text-green-600" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Almost done!</h3>
        <p className="text-gray-600 max-w-sm">
          Your email app should have opened with your details filled in. Just hit
          send and we'll get back to you shortly. If nothing opened, email us
          directly at{' '}
          <a href={`mailto:${RECIPIENT_EMAIL}`} className="text-blue-600 font-semibold">
            {RECIPIENT_EMAIL}
          </a>{' '}
          or call{' '}
          <a href="tel:+1-973-862-0606" className="text-blue-600 font-semibold">
            (973) 862-0606
          </a>.
        </p>
      </div>
    ) : (
      <>
        <h3 className="text-3xl font-bold text-gray-900 mb-2">Get Your Free Estimate</h3>
        <p className="text-gray-600 mb-8">
          Fill out the form below and we'll have someone contact you shortly to set
          up your estimate.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6" noValidate>
          {/* Full Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-semibold text-gray-800 mb-2">
              Full Name<span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Full name"
              className={`w-full px-4 py-3 rounded-xl border bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                errors.name ? 'border-red-400' : 'border-gray-200'
              }`}
            />
            {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
          </div>

          {/* Phone Number */}
          <div>
            <label htmlFor="phone" className="block text-sm font-semibold text-gray-800 mb-2">
              Phone Number<span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="Phone number"
              className={`w-full px-4 py-3 rounded-xl border bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                errors.phone ? 'border-red-400' : 'border-gray-200'
              }`}
            />
            {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
          </div>

          {/* Email Address */}
          <div>
            <label htmlFor="email" className="block text-sm font-semibold text-gray-800 mb-2">
              Email Address<span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="your.example@email.com"
              className={`w-full px-4 py-3 rounded-xl border bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                errors.email ? 'border-red-400' : 'border-gray-200'
              }`}
            />
            {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
          </div>

          {/* Service Needed */}
          <div>
            <label htmlFor="service" className="block text-sm font-semibold text-gray-800 mb-2">
              Service Needed<span className="text-red-500">*</span>
            </label>
            <select
              id="service"
              name="service"
              value={formData.service}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 rounded-xl border bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                errors.service ? 'border-red-400' : 'border-gray-200'
              } ${!formData.service ? 'text-gray-400' : ''}`}
            >
              <option value="" disabled>Select a service</option>
              {services.map((service) => (
                <option key={service} value={service} className="text-gray-900">
                  {service}
                </option>
              ))}
            </select>
            {errors.service && <p className="text-red-500 text-sm mt-1">{errors.service}</p>}
          </div>

          {/* Project Details */}
          <div>
            <label htmlFor="message" className="block text-sm font-semibold text-gray-800 mb-2">
              Project Details <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <textarea
              id="message"
              name="message"
              rows={5}
              value={formData.message}
              onChange={handleInputChange}
              placeholder="Please describe your project or issue in detail"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-y"
            />
          </div>

          {/* Emergency checkbox */}
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              name="emergency"
              checked={formData.emergency}
              onChange={handleInputChange}
              className="h-5 w-5 rounded border-gray-300 text-red-600 focus:ring-red-500"
            />
            <span className="text-sm text-gray-700">
              This is an <span className="font-semibold text-red-600">emergency</span> - I need urgent help
            </span>
          </label>

          {/* Submit */}
          <motion.button
            type="submit"
            disabled={isSubmitting}
            className="w-full inline-flex items-center justify-center space-x-2 bg-blue-600 text-white px-8 py-4 rounded-full font-bold text-lg shadow-lg hover:bg-blue-700 hover:shadow-xl transition-all duration-300 disabled:opacity-60"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Send className="h-5 w-5" />
            <span>{isSubmitting ? 'Opening your email...' : 'Send My Request'}</span>
          </motion.button>
        </form>
      </>
    )}
  </motion.div>
            {/* Contact Information */}
            <motion.div
              className="space-y-8"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              {/* Contact Details */}
              <div className="bg-gradient-to-br from-blue-50 to-white p-6 sm:p-8 rounded-3xl shadow-lg border border-blue-100">
  <h3 className="text-2xl font-bold text-gray-900 mb-6">Get In Touch</h3>
  <div className="space-y-6">
    <motion.a
      href="tel:+1-973-862-0606"
      className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-2xl hover:bg-blue-50 transition-all duration-300 group"
      whileHover={{ x: 5 }}
    >
      <div className="bg-blue-600 p-2.5 sm:p-3 rounded-xl group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
        <Phone className="h-5 sm:h-6 w-5 sm:w-6 text-white" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-gray-900 text-sm sm:text-base">24/7 Emergency Line</div>
        <div className="text-blue-600 font-bold text-base sm:text-lg">(973) 862-0606</div>
      </div>
    </motion.a>
    
    <motion.a
      href="mailto:mountoliveplumbing@gmail.com"
      className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-2xl hover:bg-blue-50 transition-all duration-300 group"
      whileHover={{ x: 5 }}
    >
      <div className="bg-blue-600 p-2.5 sm:p-3 rounded-xl group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
        <Mail className="h-5 sm:h-6 w-5 sm:w-6 text-white" />
      </div>
      <div className="min-w-0 flex-1 overflow-hidden">
        <div className="font-semibold text-gray-900 text-sm sm:text-base">Email Us</div>
        <div className="text-blue-600 text-sm sm:text-base truncate sm:break-all sm:whitespace-normal" 
             title="mountoliveplumbing@gmail.com">
          mountoliveplumbing@gmail.com
        </div>
      </div>
    </motion.a>
    
    <motion.div
      className="flex items-start gap-3 sm:gap-4 p-3 sm:p-4 rounded-2xl hover:bg-blue-50 transition-all duration-300 group"
      whileHover={{ x: 5 }}
    >
      <div className="bg-blue-600 p-2.5 sm:p-3 rounded-xl group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
        <MapPin className="h-5 sm:h-6 w-5 sm:w-6 text-white" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-gray-900 text-sm sm:text-base">Service Area</div>
        <div className="text-gray-600 text-sm sm:text-base">20-mile radius around<br className="sm:hidden" /> Budd Lake, NJ</div>
      </div>
    </motion.div>
    
    <motion.div
      className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-2xl hover:bg-blue-50 transition-all duration-300 group"
      whileHover={{ x: 5 }}
    >
      <div className="bg-blue-600 p-2.5 sm:p-3 rounded-xl group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
        <Clock className="h-5 sm:h-6 w-5 sm:w-6 text-white" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-gray-900 text-sm sm:text-base">Business Hours</div>
        <div className="text-gray-600 text-sm sm:text-base">
          Mon-Fri: 7 AM - 6 PM<br />
          Sat-Sun: 8 AM - 4 PM<br />
          <span className="text-red-600 font-semibold">Emergency Service: 24/7</span>
        </div>
      </div>
    </motion.div>
  </div>
</div>

              {/* Service Areas */}
              <div className="bg-white p-8 rounded-3xl shadow-lg border border-gray-100">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Areas We Serve</h3>
                <div className="grid grid-cols-2 gap-3">
                  {serviceAreas.map((area, index) => (
                    <motion.div
                      key={area}
                      className="flex items-center space-x-2 p-2 rounded-lg hover:bg-blue-50 transition-colors duration-300"
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{area}</span>
                    </motion.div>
                  ))}
                </div>
                <div className="mt-6 p-4 bg-blue-50 rounded-2xl">
                  <p className="text-sm text-blue-800">
                    Don't see your area listed? Give us a call - we may still be able to help!
                  </p>
                </div>
              </div>

              {/* Emergency Notice */}
              <div className="bg-gradient-to-r from-red-600 to-red-700 p-8 rounded-3xl text-white shadow-xl">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="bg-white/20 p-3 rounded-xl">
                    <AlertCircle className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">Plumbing Emergency?</h3>
                    <p className="text-red-100">We're available 24/7 for urgent repairs</p>
                  </div>
                </div>
                <motion.a
                  href="tel:+1-973-862-0606"
                  className="inline-flex items-center space-x-2 bg-white text-red-700 px-6 py-3 rounded-full font-bold shadow-lg hover:shadow-xl transition-all duration-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Phone className="h-5 w-5" />
                  <span>Call Emergency Line</span>
                </motion.a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us for Your Project */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Why Choose Us?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Here's what sets Mount Olive Plumbing apart from the competition
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Star,
                title: 'Highly Rated',
                description: '5-star reviews from satisfied customers across Morris County'
              },
              {
                icon: Clock,
                title: 'Fast Response',
                description: 'Same-day service available and 24/7 emergency response'
              },
              {
                icon: Wrench,
                title: 'Expert Technicians',
                description: 'Licensed, insured, and experienced professionals'
              },
              {
                icon: CheckCircle,
                title: 'Satisfaction Guaranteed',
                description: 'We stand behind our work with comprehensive warranties'
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                className="text-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-4 rounded-2xl w-fit mx-auto mb-4">
                  <feature.icon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;